import React, { useState, useEffect } from "react";
import {
    Box,
    TextField,
    Button,
    Typography,
    CircularProgress,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from "@mui/material";
import axios from "axios";
import USDARecipeCard from "../../components/Cards/USDARecipeCard";

const RecipeSuggestor = ({ fdcId, onClose }) => {
    const [loading, setLoading] = useState(false);
    const [productInfo, setProductInfo] = useState(null);
    const [error, setError] = useState("");
    const [servings, setServings] = useState("50");
    const [program, setProgram] = useState("NSLP");
    const [ageGroup, setAgeGroup] = useState("Middle"); // "Elementary", "Middle", "High"
    const [mealService, setMealService] = useState("Lunch");
    const [aiRawRecipeText, setAiRawRecipeText] = useState("");
    const [parsedRecipe, setParsedRecipe] = useState(null);
    const [saveSuccess, setSaveSuccess] = useState("");

    // USDA API key to fetch product info remains
    const usdaApiKey = "u5A7qQBkgjfxOEi0ec5xDWwU0WcSa3SDOg2dMPYG";

    // --------------------------------------------------------------------------
    // 1) Fetch product info from USDA once the component mounts or fdcId changes
    // --------------------------------------------------------------------------
    useEffect(() => {
        fetchProductInfo();
    }, [fdcId]);

    const fetchProductInfo = async () => {
        if (!fdcId) {
            setError("Invalid FDC ID. Cannot fetch product information.");
            return;
        }
        const usdaApiUrl = `https://api.nal.usda.gov/fdc/v1/food/${fdcId}?format=full&api_key=${usdaApiKey}`;
        setLoading(true);
        setError("");
        try {
            const response = await axios.get(usdaApiUrl);
            if (response.status === 200 && response.data) {
                setProductInfo(response.data);
            } else {
                setError("Product information could not be retrieved.");
            }
        } catch (err) {
            setError(err.response?.data?.message || "Failed to fetch product information.");
        } finally {
            setLoading(false);
        }
    };

    // --------------------------------------------------------------------------
    // 2) Post the generated recipe to your custom API/database
    // --------------------------------------------------------------------------
    const postToCustomAPI = async (parsedRecipeObject) => {
        try {
            const payload = {
                // MUST match your new DB fields exactly:
                apiid: "D0BA991F-9907-461A-93D9-D046FE534252", // primary key, set or let the DB default
                SchoolDistrictID: "D0BA991F-9907-461A-93D9-D046FE534252",
                SchoolDistrictName: "Orange County Public Schools",
                ProductName: productInfo?.description || "",
                RecipeName: parsedRecipeObject?.name || "",
                MealService: mealService,
                ProgramName: program,
                AgeGroup: ageGroup,
                fdcid: fdcId?.toString() || "",
                recipe: JSON.stringify(parsedRecipeObject),
                CreatedOn: new Date().toISOString(),
                isActive: true,
            };

            console.log("Payload to DB:", payload);

            // POST to your new API endpoint (unchanged)
            const response = await axios.post(
                "https://api.swancreate.com/api/llm/ai_usda_recipe",
                payload,
                { headers: { "Content-Type": "application/json" } }
            );

            console.log("Data successfully posted to custom API:", response.data);
            setSaveSuccess("Recipe successfully saved to database!");
        } catch (error) {
            console.error("Error posting to custom API:", error);
            setSaveSuccess("");
        }
    };

    // --------------------------------------------------------------------------
    // 3) Generate recipe via the new AI endpoint
    // --------------------------------------------------------------------------
    const generateRecipe = async () => {
        setLoading(true);
        setError("");
        setAiRawRecipeText("");
        setParsedRecipe(null);
        setSaveSuccess("");

        try {
            // Combine nutritional details (from branded label and foodNutrients) if available
            let combinedNutrients = [];
            if (productInfo?.dataType === "Branded" && productInfo.labelNutrients) {
                const labelNutrientsArray = Object.entries(productInfo.labelNutrients).map(
                    ([labelName, nutrientData]) => ({
                        nutrientName: labelName,
                        value: nutrientData.value,
                        unitName: nutrientData.unitName || "g",
                    })
                );
                combinedNutrients = labelNutrientsArray;
            }
            if (productInfo?.foodNutrients?.length) {
                combinedNutrients = [...combinedNutrients, ...productInfo.foodNutrients];
            }

            // System instruction for recipe generation
            const systemMessage =
                "You are a recipe assistant. Generate a USDA-compliant recipe in valid JSON format.";

            // Build the user prompt with provided inputs and product info
            const userMessage = `
Generate a USDA-compliant recipe for "${productInfo?.description || "this product"}" with ${servings || "50"} servings.
This recipe must qualify as a reimbursable meal under the ${program || "NSLP"} program and be suitable for ${ageGroup} students.
Ensure that the recipe is for a "${mealService || "Lunch"}" service and meets all meal pattern requirements, including the inclusion of protein, grains, fruits, vegetables, and dairy.
If this product alone does not meet the requirements, add other known K-12 products to create a complete reimbursable meal.
Nutritional details: ${JSON.stringify(combinedNutrients)}
Return ONLY valid JSON (no extra text) in the following structure:
{
  "name": "string - name of the recipe",
  "servings": "number or string - how many servings the recipe makes",
  "time": "string - approximate total time, e.g., '45 minutes'",
  "ingredients": [
    {
      "name": "string - ingredient name",
      "quantity": "number (or string) - amount needed",
      "unit": "string - unit of measure, e.g. 'cups', 'tbsp'"
    }
  ],
  "instructions": [
    "Step 1 instruction",
    "Step 2 instruction",
    "..."
  ],
  "nutrition": {
    "Calories": "xxx per serving",
    "Protein": "yyy g",
    "...": "other relevant nutrients"
  },
  "compliance": "string - explain how this recipe complies with the program ('${program || "NSLP"}')."
}`;

            // Construct final prompt by combining system and user prompts
            const finalPrompt = `${systemMessage}\n${userMessage}`;

            // Send the prompt to your new AI endpoint
            const response = await axios.post(
                "https://aiendpoint.swancreate.com/api/ai/gpt",
                { prompt: finalPrompt },
                { headers: { "Content-Type": "application/json" } }
            );
            const rawRecipeText = response.data.response.trim();
            setAiRawRecipeText(rawRecipeText);

            try {
                // Attempt to parse the JSON response
                const parsed = JSON.parse(rawRecipeText);
                setParsedRecipe(parsed);
                // Immediately log the generated recipe to the DB
                await postToCustomAPI(parsed);
            } catch (jsonErr) {
                setError("AI returned invalid JSON. Please see raw text below.");
            }
        } catch (err) {
            setError(err.response?.data?.error?.message || "Failed to generate recipe. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ backgroundColor: "white", padding: 3, borderRadius: 2 }}>
            <Typography variant="h5" gutterBottom>
                Recipe Suggestor
            </Typography>

            {error && (
                <Typography variant="body1" color="red" gutterBottom>
                    {error}
                </Typography>
            )}

            {saveSuccess && (
                <Typography variant="body1" color="green" gutterBottom>
                    {saveSuccess}
                </Typography>
            )}

            {loading && <CircularProgress color="secondary" />}

            {/* If product info is loaded and we haven't parsed the AI recipe yet, show the form */}
            {productInfo && !parsedRecipe && (
                <Box>
                    <Paper sx={{ backgroundColor: "#f5f5f5", padding: 2, marginBottom: 2, borderRadius: 2 }}>
                        <Typography variant="h6" gutterBottom>
                            {productInfo.description || "Product Name Not Available"}
                        </Typography>
                    </Paper>

                    <TextField
                        label="Number of Servings"
                        type="number"
                        value={servings}
                        onChange={(e) => setServings(e.target.value)}
                        fullWidth
                        sx={{ marginBottom: 2 }}
                    />

                    {/* Program selection */}
                    <FormControl fullWidth sx={{ marginBottom: 2 }}>
                        <InputLabel>Program</InputLabel>
                        <Select value={program} onChange={(e) => setProgram(e.target.value)}>
                            <MenuItem value="NSLP">National School Lunch Program (NSLP)</MenuItem>
                            <MenuItem value="SSFP">Summer Food Service Program (SSFP)</MenuItem>
                            <MenuItem value="CACFP">Child and Adult Care Food Program (CACFP)</MenuItem>
                        </Select>
                    </FormControl>

                    {/* Age Group selection */}
                    <FormControl fullWidth sx={{ marginBottom: 2 }}>
                        <InputLabel>Age Group</InputLabel>
                        <Select value={ageGroup} onChange={(e) => setAgeGroup(e.target.value)}>
                            <MenuItem value="Elementary">Elementary</MenuItem>
                            <MenuItem value="Middle">Middle</MenuItem>
                            <MenuItem value="High">High</MenuItem>
                        </Select>
                    </FormControl>

                    {/* Meal Service selection */}
                    <FormControl fullWidth sx={{ marginBottom: 2 }}>
                        <InputLabel>Meal Service</InputLabel>
                        <Select value={mealService} onChange={(e) => setMealService(e.target.value)}>
                            <MenuItem value="Breakfast">Breakfast</MenuItem>
                            <MenuItem value="Lunch">Lunch</MenuItem>
                            <MenuItem value="Snack">Snack</MenuItem>
                            <MenuItem value="Supper">Supper</MenuItem>
                        </Select>
                    </FormControl>

                    <Button variant="contained" color="primary" onClick={generateRecipe}>
                        Generate Recipe
                    </Button>
                </Box>
            )}

            {/* If the recipe was parsed, show the USDARecipeCard */}
            {parsedRecipe && <USDARecipeCard recipe={parsedRecipe} />}
        </Box>
    );
};

export default RecipeSuggestor;
